// ═══════════════════════════════════════
// ATLAS BEASTS — PVP ARENA
// Pokemon Go-style mobile overlay
// ═══════════════════════════════════════

import { useState } from 'react'

const RANKS = [
  { id: 'bronze', label: 'Bronze', color: '#CD7F32', minRating: 0 },
  { id: 'silver', label: 'Silver', color: '#C0C0C0', minRating: 1000 },
  { id: 'gold', label: 'Gold', color: '#FFD700', minRating: 2000 },
  { id: 'platinum', label: 'Platinum', color: '#E5E4E2', minRating: 3000 },
  { id: 'diamond', label: 'Diamond', color: '#B9F2FF', minRating: 4000 },
  { id: 'champion', label: 'Champion', color: '#FF4800', minRating: 5000 },
]

const SAMPLE_PLAYERS = [
  { id: '1', name: 'DragonSlayer99', rank: 'diamond', rating: 4250, wins: 142, losses: 38, streak: 5 },
  { id: '2', name: 'BeastMaster', rank: 'platinum', rating: 3780, wins: 98, losses: 45, streak: 2 },
  { id: '3', name: 'VoidWalker', rank: 'gold', rating: 2450, wins: 76, losses: 52, streak: 0 },
  { id: '4', name: 'FireStorm', rank: 'silver', rating: 1560, wins: 54, losses: 41, streak: 3 },
  { id: '5', name: 'IceQueen', rank: 'bronze', rating: 890, wins: 32, losses: 28, streak: 1 },
]

export default function PVP() {
  const [selectedTab, setSelectedTab] = useState<'ranked' | 'quick' | 'friends'>('ranked')
  const [selectedOpponent, setSelectedOpponent] = useState<typeof SAMPLE_PLAYERS[0] | null>(null)
  const [currentRating] = useState(2150) // User's current rating

  const currentRank = RANKS.slice().reverse().find(r => currentRating >= r.minRating)
  const nextRank = RANKS[RANKS.findIndex(r => r.id === currentRank?.id) + 1]

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Arena</h1>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[10px] text-[#FFE800]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {currentRating} ELO
          </span>
        </div>
      </div>

      {/* Rank Display */}
      <div className="p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)', background: 'linear-gradient(180deg, rgba(255,232,0,0.05) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-[#5A5A7A] uppercase tracking-wider mb-1" style={{ fontFamily: 'Space Mono, monospace' }}>Current Rank</p>
            <p 
              className="font-bold text-2xl uppercase" 
              style={{ 
                color: currentRank?.color, 
                fontFamily: 'Rajdhani, system-ui',
                textShadow: `0 0 20px ${currentRank?.color}40`,
              }}
            >
              {currentRank?.label}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#5A5A7A] uppercase tracking-wider mb-1" style={{ fontFamily: 'Space Mono, monospace' }}>Next Rank</p>
            <p className="font-bold text-sm text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
              {nextRank?.label}
            </p>
            <p className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
              {nextRank?.minRating && currentRating < nextRank.minRating ? `${nextRank.minRating - currentRating} to go` : 'Max'}
            </p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3">
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(58,58,90,0.3)' }}>
            <div 
              className="h-full transition-all duration-500"
              style={{ 
                width: nextRank?.minRating ? `${Math.min(100, (currentRating / nextRank.minRating) * 100)}%` : '100%',
                background: `linear-gradient(90deg, ${currentRank?.color}60, ${currentRank?.color})`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {[
          { id: 'ranked', label: 'Ranked', icon: '🏆' },
          { id: 'quick', label: 'Quick Match', icon: '⚡' },
          { id: 'friends', label: 'Friends', icon: '👥' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id as any)}
            className={`font-bold text-xs px-4 py-2 rounded-lg border transition-all bg-transparent cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              selectedTab === tab.id
                ? 'border-[#FFE800] border-opacity-60 text-[#FFE800] bg-[#FFE800] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedTab === 'ranked' && (
          <div className="space-y-3">
            <p className="text-[10px] text-[#5A5A7A] uppercase tracking-wider mb-2" style={{ fontFamily: 'Space Mono, monospace' }}>
              Top Players
            </p>
            {SAMPLE_PLAYERS.map((player, index) => (
              <OpponentCard key={player.id} player={player} rank={index + 1} onSelect={setSelectedOpponent} />
            ))}
          </div>
        )}

        {selectedTab === 'quick' && (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <span className="text-5xl mb-4 block">⚡</span>
            <p className="font-bold text-lg text-[#EAF6FF] uppercase mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>
              Quick Match
            </p>
            <p className="text-sm text-[#8A8AA5] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Find an opponent near your skill level
            </p>
            <button
              className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ 
                background: 'linear-gradient(135deg, #FF4800 0%, #FF480080 100%)',
                color: '#EAF6FF',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              Find Match
            </button>
          </div>
        )}

        {selectedTab === 'friends' && (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <span className="text-5xl mb-4 block">👥</span>
            <p className="font-bold text-lg text-[#EAF6FF] uppercase mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>
              Challenge Friends
            </p>
            <p className="text-sm text-[#8A8AA5] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              No friends online right now
            </p>
          </div>
        )}
      </div>

      {/* Opponent Detail Modal */}
      {selectedOpponent && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(2,5,16,0.9)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <button
              onClick={() => setSelectedOpponent(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-[#8A8AA5] hover:text-[#EAF6FF]"
              style={{ border: '1px solid rgba(58,58,90,0.5)' }}
            >
              ✕
            </button>
            
            <div className="text-center mb-4">
              <p className="text-4xl mb-2">⚔️</p>
              <h2 className="font-bold text-xl text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {selectedOpponent.name}
              </h2>
              <span 
                className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase mt-2"
                style={{ 
                  color: RANKS.find(r => r.id === selectedOpponent.rank)?.color,
                  background: `${RANKS.find(r => r.id === selectedOpponent.rank)?.color}15`,
                  border: `1px solid ${RANKS.find(r => r.id === selectedOpponent.rank)?.color}40`,
                  fontFamily: 'Space Mono, monospace',
                }}
              >
                {RANKS.find(r => r.id === selectedOpponent.rank)?.label}
              </span>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Rating</span>
                <span className="font-bold text-lg text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{selectedOpponent.rating}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>W/L</span>
                <span className="font-bold text-sm text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                  {selectedOpponent.wins} / {selectedOpponent.losses}
                </span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Win Streak</span>
                <span className="font-bold text-sm text-[#39FF14]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                  {selectedOpponent.streak > 0 ? `${selectedOpponent.streak} 🔥` : '-'}
                </span>
              </div>
            </div>

            <button
              onClick={() => setSelectedOpponent(null)}
              className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ 
                background: 'linear-gradient(135deg, #FF4800 0%, #FF480080 100%)',
                color: '#EAF6FF',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              Challenge
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function OpponentCard({ player, rank, onSelect }: { player: typeof SAMPLE_PLAYERS[0]; rank: number; onSelect: (player: typeof SAMPLE_PLAYERS[0]) => void }) {
  const rankColor = RANKS.find(r => r.id === player.rank)?.color

  return (
    <button
      onClick={() => onSelect(player)}
      className="rounded-xl border p-4 transition-all active:scale-95 hover:scale-105 text-left flex items-center gap-4"
      style={{ 
        background: 'rgba(13,13,21,0.95)', 
        borderColor: `${rankColor}40`,
        boxShadow: `0 0 12px ${rankColor}15`,
      }}
    >
      <div className="flex-shrink-0">
        <span 
          className="text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-full"
          style={{ 
            color: rankColor,
            background: `${rankColor}15`,
            fontFamily: 'Rajdhani, system-ui',
          }}
        >
          #{rank}
        </span>
      </div>
      <div className="flex-1">
        <p className="font-bold text-sm text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
          {player.name}
        </p>
        <div className="flex items-center gap-3 mt-1">
          <span 
            className="text-[10px] font-bold uppercase"
            style={{ 
              color: rankColor,
              fontFamily: 'Space Mono, monospace',
            }}
          >
            {player.rank}
          </span>
          <span className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {player.rating} ELO
          </span>
        </div>
      </div>
      <div className="text-right">
        <p className="text-[10px] text-[#5A5A7A] uppercase" style={{ fontFamily: 'Space Mono, monospace' }}>
          W/L
        </p>
        <p className="font-bold text-sm text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
          {player.wins}/{player.losses}
        </p>
      </div>
    </button>
  )
}
